export type RuntimeDb = "airtable" | "supabase";

export type RuntimeDbSelectionSource = "url" | "environment" | "fallback";

export interface RuntimeDbSelection {
  db: RuntimeDb;
  source: RuntimeDbSelectionSource;
}

function normalizeDb(value: string | null | undefined): RuntimeDb | null {
  const normalized = value?.trim().toLowerCase();
  return normalized === "airtable" || normalized === "supabase"
    ? normalized
    : null;
}

function queryDb(search: string): RuntimeDb | null {
  try {
    return normalizeDb(new URLSearchParams(search).get("db"));
  } catch {
    return null;
  }
}

export function resolveRuntimeDb(
  search: string,
  configuredDefault?: string,
): RuntimeDbSelection {
  const fromUrl = queryDb(search);
  if (fromUrl) return { db: fromUrl, source: "url" };

  const fromEnvironment = normalizeDb(configuredDefault);
  if (fromEnvironment) {
    return { db: fromEnvironment, source: "environment" };
  }

  return { db: "airtable", source: "fallback" };
}

function configured(value: string | undefined): boolean {
  return Boolean(value?.trim());
}

export function isDatabaseRoutingConfigured({
  search,
  configuredDefault,
  pairedUrls,
}: {
  search: string;
  configuredDefault?: string;
  pairedUrls: Array<string | undefined>;
}): boolean {
  return (
    queryDb(search) !== null ||
    normalizeDb(configuredDefault) !== null ||
    pairedUrls.some(configured)
  );
}

export function selectDatabaseEndpoint({
  db,
  airtableUrl,
  supabaseUrl,
  legacyUrl,
  databaseRoutingConfigured,
}: {
  db: RuntimeDb;
  airtableUrl?: string;
  supabaseUrl?: string;
  legacyUrl?: string;
  databaseRoutingConfigured: boolean;
}): string | undefined {
  if (!databaseRoutingConfigured) return legacyUrl?.trim() || undefined;

  const selected = db === "supabase" ? supabaseUrl : airtableUrl;
  return selected?.trim() || undefined;
}

const browserSearch =
  typeof window === "undefined" ? "" : window.location.search;

const pairedUrls = [
  process.env.REACT_APP_N8N_WEBHOOK_V3_AIRTABLE_URL,
  process.env.REACT_APP_N8N_WEBHOOK_V3_SUPABASE_URL,
  process.env.REACT_APP_N8N_INIT_AIRTABLE_URL,
  process.env.REACT_APP_N8N_INIT_SUPABASE_URL,
  process.env.REACT_APP_N8N_UPDATE_COINS_ITEMS_AIRTABLE_URL,
  process.env.REACT_APP_N8N_UPDATE_COINS_ITEMS_SUPABASE_URL,
];

export const RUNTIME_DB_SELECTION = resolveRuntimeDb(
  browserSearch,
  process.env.REACT_APP_DATA_BACKEND,
);

export const RUNTIME_DB = RUNTIME_DB_SELECTION.db;

export const DATABASE_ROUTING_CONFIGURED = isDatabaseRoutingConfigured({
  search: browserSearch,
  configuredDefault: process.env.REACT_APP_DATA_BACKEND,
  pairedUrls,
});
